import { TypedDocumentNode, gql } from '@apollo/client';
import {
  faBullseyeArrow,
  faDoorOpen,
  faHandsHelping,
} from '@fortawesome/pro-solid-svg-icons';
import { Helmet } from 'react-helmet';
import { FormattedDate, FormattedMessage } from 'react-intl';
import { generatePath } from 'react-router-dom';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import {
  AveragePlayerScore,
  Position,
} from '@sorare/core/src/__generated__/globalTypes';
import { FullWidthContainer } from '@sorare/core/src/atoms/layout/FullWidthContainer';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyM, HeadlineS, LabelS } from '@sorare/core/src/atoms/typography';
import { Breadcrumb } from '@sorare/core/src/components/breadcrumb';
import {
  MainAsideLayout,
  Section,
} from '@sorare/core/src/components/layout/MainAsideLayout';
import { PlayerScore } from '@sorare/core/src/components/scoring/PlayerScore';
import { Header } from '@sorare/core/src/components/seo/Header';
import { OfficialProduct } from '@sorare/core/src/components/seo/OfficialProduct';
import { CardSupply } from '@sorare/core/src/components/seo/Player/CardSupply';
import { Infos, messages } from '@sorare/core/src/components/seo/Player/Infos';
import { Country } from '@sorare/core/src/components/seo/Player/Infos/Country';
import {
  MoreAboutPlayer,
  messages as playerMoreAboutMessages,
} from '@sorare/core/src/components/seo/Player/MoreAboutPlayer';
import { RelatedPlayers } from '@sorare/core/src/components/seo/Player/RelatedPlayers';
import { Transfers } from '@sorare/core/src/components/seo/Player/Transfers';
import { Avatar } from '@sorare/core/src/components/user/Avatar';
import { UserName } from '@sorare/core/src/components/user/UserName';
import { FOOTBALL_MY_CLUB_SLUG } from '@sorare/core/src/constants/__generated__/routes';
import {
  FOOTBALL_CLUB_SHOW,
  FOOTBALL_COUNTRY_SHOW,
  FOOTBALL_HOME,
  FOOTBALL_LEAGUE_SHOW,
  FOOTBALL_PLAYER_SHOW,
} from '@sorare/core/src/constants/routes';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import { isType } from '@sorare/core/src/gql';
import useScreenSize from '@sorare/core/src/hooks/device/useScreenSize';
import { useIntlCountries } from '@sorare/core/src/hooks/useIntlCountries';
import { useTitleAndDescription } from '@sorare/core/src/hooks/useTitleAndDescription';
import { FILTERS } from '@sorare/core/src/lib/filters';
import {
  cardAttributes,
  fantasy,
  glossary,
  playerDetails,
  userAttributes,
} from '@sorare/core/src/lib/glossary';
import { withRouteQuery } from '@sorare/core/src/lib/navigation/withRouteQuery';
import {
  getAppearancePercentage,
  playingStatusNames,
  positionNames,
} from '@sorare/core/src/lib/players';
import { metadatas } from '@sorare/core/src/lib/seo/football';

import { U23Eligible } from '@sorare/marketplace/src/components/card/U23Eligible';
import { PriceGraph } from '@sorare/marketplace/src/components/price/PriceGraph';

import { LazyFixtureChart } from '@sorare/gameplay/src/components/stats/LastScores/LazyFixtureChart';

import { CareerStats } from './Overview/CareerStats';
import { GamesNavigator } from './Overview/GamesNavigator';
import { UpcomingGame } from './Overview/UpcomingGame';
import {
  FootballPlayerOverviewQuery,
  FootballPlayerOverviewQueryVariables,
} from './__generated__/page.graphql';
import { PageParams } from './__generated__/routeParams';

const FOOTBALL_PLAYER_OVERVIEW_QUERY = gql`
  query FootballPlayerOverviewQuery($slug: String!) {
    anyPlayer(slug: $slug) {
      slug
      displayName
      firstName
      lastName
      cardSupply {
        season {
          startYear
        }
      }
      avatarPictureUrl
      ...UpcomingGame_player
      activeClub {
        slug
        name
        pictureUrl
        lastFiveGames {
          id
          date
          status: statusTyped
          competition {
            slug
            displayName
          }
          homeTeam {
            slug
            name
            pictureUrl
          }
          homeGoals
          awayTeam {
            slug
            name
            pictureUrl
          }
          awayGoals
        }
        domesticLeague {
          slug
          displayName
          pictureUrl
          released
        }
        upcomingGames(first: 1) {
          id
          ...UpcomingGame_game
        }
      }
      activeClubMembership {
        id
        aggregatedStats {
          id
          goals
        }
      }
      activeNationalTeam {
        slug
        name
        country {
          flagUrl
          slug
          code
          name
        }
      }
      age
      height
      country {
        slug
        code
        name
        flagUrl
        threeLetterCode
      }
      clubMemberships {
        id
        startDate
        endDate
        team {
          slug
          name
          pictureUrl
        }
        aggregatedStats {
          id
          appearances
          goals
          assists
        }
      }
      shirtNumber
      user {
        slug
        ...Avatar_publicUserInfoInterface
        ...UserName_publicUserInfoInterface
      }
      ...CardSupply_anyPlayerInterface
      ...Header_subscription
      ... on Player {
        slug
        u23Eligible
        u23EligibleUntil
        squaredPictureUrl: pictureUrl(derivative: "squared")
        position
        rankingForPositionLeague
        birthDate
        playingStatus
        activeInjuries {
          id
          expectedEndDate
          kind
          startDate
        }
        activeSuspensions {
          id
          startDate
          endDate
          reason
        }
        relatedPlayers {
          slug
          displayName
          pictureUrl: pictureUrl(derivative: "avatar")
          age
          position
          country {
            slug
            code
            name
            threeLetterCode
          }
          ...getAppearancePercentage_anyPlayerInterface
          ...PlayerScore_anyPlayer
        }
        ...CareerStats_player
        ...PlayerScore_anyPlayer
        ...getAppearancePercentage_anyPlayerInterface
      }
    }
  }
  ${CareerStats.fragments.player}
  ${Avatar.fragments.publicUserInfoInterface}
  ${UserName.fragments.user}
  ${CardSupply.fragments.anyPlayer}
  ${UpcomingGame.fragments.game}
  ${UpcomingGame.fragments.player}
  ${Header.fragments.subscription}
  ${PlayerScore.fragments.anyPlayer}
  ${getAppearancePercentage.fragments.anyPlayer}
` as TypedDocumentNode<
  FootballPlayerOverviewQuery,
  FootballPlayerOverviewQueryVariables
>;

const StyledVertical = styled(Vertical)`
  align-items: end;
`;

export default withRouteQuery(
  ({ queryResult }) => {
    const { formatNumber, formatMessage, formatDate } = useIntlContext();
    const countries = useIntlCountries();
    const { up: isLaptopAndAbove } = useScreenSize('laptop');
    const { data } = queryResult;

    useTitleAndDescription(
      metadatas.playerOverview.title,
      metadatas.playerOverview.description,
      {
        player: data?.anyPlayer.displayName,
      },
      {
        img: data?.anyPlayer.avatarPictureUrl,
      }
    );

    if (!data) return null;

    const player = data.anyPlayer;
    if (!isType(player, 'Player')) return null;

    const playersBreadcrumb = [
      {
        to: FOOTBALL_HOME,
        name: 'Football',
      },
      {
        name: formatMessage(glossary.players),
      },
      { name: player.displayName },
    ];

    const clubMembershipWithAppearences = player.clubMemberships.filter(
      membership => !!membership.aggregatedStats.appearances
    );

    const blocks = {
      header: (
        <>
          <Breadcrumb sections={playersBreadcrumb} />
          <Header
            id={player.slug}
            displayName={player.displayName}
            pictureUrl={player.squaredPictureUrl}
            memberships={[
              {
                name: player.country.name,
                pictureUrl: player.country.flagUrl,
                to: generatePath(FOOTBALL_COUNTRY_SHOW, {
                  countryCode: player.country.code,
                }),
              },
              ...(player.activeClub
                ? [
                    ...(player.activeClub.domesticLeague
                      ? [
                          {
                            name: player.activeClub.domesticLeague.displayName,
                            pictureUrl:
                              player.activeClub.domesticLeague.pictureUrl,
                            to: generatePath(FOOTBALL_LEAGUE_SHOW, {
                              slug: player.activeClub.domesticLeague.slug,
                            }),
                            verified: player.activeClub.domesticLeague.released,
                          },
                        ]
                      : []),
                    {
                      name: player.activeClub.name,
                      pictureUrl: player.activeClub.pictureUrl,
                      to: generatePath(FOOTBALL_CLUB_SHOW, {
                        slug: player.activeClub.slug,
                      }),
                    },
                  ]
                : []),
            ]}
            scores={[
              {
                label: <FormattedMessage {...cardAttributes.last5} />,
                body: (
                  <PlayerScore
                    player={player}
                    mode={AveragePlayerScore.LAST_FIVE_SO5_AVERAGE_SCORE}
                  />
                ),
                subLabel: (
                  <FormattedMessage
                    {...fantasy.percentagePlayed}
                    values={{
                      percent:
                        +getAppearancePercentage(
                          AveragePlayerScore.LAST_FIVE_SO5_AVERAGE_SCORE,
                          player
                        ) / 100,
                    }}
                  />
                ),
              },
              {
                label: <FormattedMessage {...cardAttributes.last15} />,
                body: (
                  <PlayerScore
                    player={player}
                    mode={AveragePlayerScore.LAST_FIFTEEN_SO5_AVERAGE_SCORE}
                  />
                ),
                subLabel: (
                  <FormattedMessage
                    {...fantasy.percentagePlayed}
                    values={{
                      percent:
                        +getAppearancePercentage(
                          AveragePlayerScore.LAST_FIFTEEN_SO5_AVERAGE_SCORE,
                          player
                        ) / 100,
                    }}
                  />
                ),
              },
              player.rankingForPositionLeague && {
                label: <FormattedMessage {...playerDetails.leagueRanking} />,
                body: <>#{player.rankingForPositionLeague || 0}</>,
                subLabel: (
                  <FormattedMessage {...positionNames[player.position]} />
                ),
              },
            ].filter(Boolean)}
            subscription={player}
            upcomingGame={
              player.activeClub?.upcomingGames?.[0] && (
                <UpcomingGame
                  player={player}
                  game={player.activeClub.upcomingGames[0]}
                />
              )
            }
          />
          <Helmet
            script={[
              {
                type: 'application/ld+json',
                innerHTML: JSON.stringify({
                  '@context': 'https://schema.org/',
                  '@type': 'Person',
                  name: player.displayName,
                  givenName: player.firstName,
                  familyName: player.lastName,
                  ...(player.birthDate
                    ? {
                        birthDate: formatDate(player.birthDate, {
                          timeZone: 'UTC',
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                        }),
                      }
                    : {}),
                  ...(player.height
                    ? {
                        height: `${formatNumber(player.height / 1e2, {
                          maximumFractionDigits: 2,
                          minimumFractionDigits: 2,
                        })} m`,
                      }
                    : {}),
                  nationality: countries.find(
                    n => n.value === player?.country.code
                  )?.label,
                  image: player.squaredPictureUrl,
                  mainEntityOfPage: generatePath(FOOTBALL_PLAYER_SHOW, {
                    slug: player.slug,
                  }),
                  affiliation: [
                    ...(player.activeClub
                      ? [
                          {
                            '@type': 'SportsTeam',
                            name: player.activeClub.name,
                            mainEntityOfPage: generatePath(FOOTBALL_CLUB_SHOW, {
                              slug: player.activeClub.slug,
                            }),
                          },
                        ]
                      : []),
                    ...(player.activeNationalTeam
                      ? [
                          {
                            '@type': 'SportsTeam',
                            name: player.activeNationalTeam.name,
                          },
                        ]
                      : []),
                  ],
                }),
              },
            ]}
          />
        </>
      ),
      infos: (
        <Infos
          informations={[
            {
              label: cardAttributes.position,
              value: <FormattedMessage {...positionNames[player.position]} />,
            },
            player.birthDate && {
              label: playerDetails.birthDateAndAge,
              value: (
                <Horizontal>
                  <FormattedDate
                    timeZone="UTC"
                    value={player.birthDate}
                    day="2-digit"
                    month="2-digit"
                    year="numeric"
                  />{' '}
                  ({player.age})
                </Horizontal>
              ),
            },
            player.height && {
              label: cardAttributes.height,
              value: (
                <FormattedMessage
                  {...messages.playerHeight}
                  values={{ height: player.height / 100 }}
                />
              ),
            },
            player.playingStatus && {
              label: userAttributes.status,
              value: (
                <FormattedMessage
                  {...playingStatusNames[player.playingStatus]}
                />
              ),
            },
            {
              label: glossary.nationality,
              value: (
                <Link
                  to={generatePath(FOOTBALL_COUNTRY_SHOW, {
                    countryCode: player.country.code,
                  })}
                >
                  <Country {...player.country} />
                </Link>
              ),
            },
            player.user && {
              label: glossary.manager,
              value: (
                <Link
                  to={generatePath(FOOTBALL_MY_CLUB_SLUG, {
                    slug: player.user.slug,
                  })}
                >
                  <Horizontal>
                    <Avatar user={player.user} size={3} />
                    <BodyM as="p" color="var(--c-white)" bold>
                      <UserName user={player.user} />
                    </BodyM>
                  </Horizontal>
                </Link>
              ),
            },
            player.u23Eligible && {
              label: cardAttributes.eligibility,
              value: <U23Eligible until={player.u23EligibleUntil} />,
            },
          ].filter(Boolean)}
          injury={
            player.activeInjuries[0] && {
              date: player.activeInjuries[0]?.startDate,
              reason: player.activeInjuries[0]?.kind,
              expectedEndDate: player.activeInjuries[0]?.expectedEndDate,
            }
          }
          suspension={
            player.activeSuspensions[0] && {
              date: player.activeSuspensions[0]?.startDate,
              reason: player.activeSuspensions[0]?.reason,
              expectedEndDate: player.activeSuspensions[0]?.endDate,
            }
          }
        />
      ),
      performance: (
        <FullWidthContainer>
          <LazyFixtureChart
            playerSlug={player.slug}
            TitleComponent={HeadlineS}
          />
        </FullWidthContainer>
      ),
      games: <GamesNavigator key={player.slug} playerSlug={player.slug} />,
      careerStats: <CareerStats player={player} />,
      transfers: !!clubMembershipWithAppearences.length && (
        <Transfers
          displayName={player.displayName}
          stats={['appearances', 'goals', 'assists']}
          icons={{
            appearances: faDoorOpen,
            goals: faBullseyeArrow,
            assists: faHandsHelping,
          }}
          transfers={clubMembershipWithAppearences.map(membership => ({
            ...membership,
            team: {
              ...membership.team,
              to: generatePath(FOOTBALL_CLUB_SHOW, {
                slug: membership.team.slug,
              }),
            },
            stats: membership.aggregatedStats,
          }))}
        />
      ),
      relatedPlayers: (
        <RelatedPlayers
          stats={['l5', 'l15']}
          headers={{
            l5: FILTERS.lastFiveAverageScore.alias,
            l15: FILTERS.lastFifteenAverageScore.alias,
          }}
          players={player.relatedPlayers.map(relatedPlayer => ({
            ...relatedPlayer,
            stats: {
              l5: (
                <StyledVertical gap={0.5}>
                  <PlayerScore
                    player={relatedPlayer}
                    mode={AveragePlayerScore.LAST_FIVE_SO5_AVERAGE_SCORE}
                  />
                  <LabelS as="p" color="var(--c-nd-600)">
                    {getAppearancePercentage(
                      AveragePlayerScore.LAST_FIVE_SO5_AVERAGE_SCORE,
                      relatedPlayer
                    )}
                    %
                  </LabelS>
                </StyledVertical>
              ),
              l15: (
                <StyledVertical gap={0.5}>
                  <PlayerScore
                    player={relatedPlayer}
                    mode={AveragePlayerScore.LAST_FIFTEEN_SO5_AVERAGE_SCORE}
                  />
                  <LabelS as="p" color="var(--c-nd-600)">
                    {getAppearancePercentage(
                      AveragePlayerScore.LAST_FIFTEEN_SO5_AVERAGE_SCORE,
                      relatedPlayer
                    )}
                    %
                  </LabelS>
                </StyledVertical>
              ),
            },
            to: generatePath(FOOTBALL_PLAYER_SHOW, {
              slug: relatedPlayer.slug,
            }),
          }))}
        />
      ),
      moreAbout: (
        <MoreAboutPlayer
          displayName={player.displayName}
          club={player.activeClub?.name}
          nationalTeamCode={player.activeNationalTeam?.country.code}
          shirtNumber={player.shirtNumber}
          age={player.age}
          birthDate={player.birthDate}
          height={player.height}
          countryCode={player.country.code}
          specificItems={[
            player.position === Position.Forward &&
              player.activeClub &&
              player.activeClubMembership && {
                question: formatMessage(playerMoreAboutMessages.howManyGoals, {
                  displayName: player.displayName,
                }),
                answer: formatMessage(
                  playerMoreAboutMessages.howManyGoalsAnswer,
                  {
                    displayName: player.displayName,
                    club: player.activeClub?.name,
                    goals: player.activeClubMembership?.aggregatedStats.goals,
                  }
                ),
              },
          ].filter(Boolean)}
        />
      ),
      priceGraph: <PriceGraph playerSlug={player.slug} withSeasonEligibility />,
      officialProduct: player.activeClub?.domesticLeague?.released && (
        <OfficialProduct
          displayName={player.activeClub?.domesticLeague.displayName}
          pictureUrl={player.activeClub?.domesticLeague.pictureUrl}
          to={generatePath(FOOTBALL_LEAGUE_SHOW, {
            slug: player.activeClub?.domesticLeague.slug,
          })}
        />
      ),
      cardSupply: <CardSupply anyPlayer={player} />,
    };

    if (isLaptopAndAbove) {
      return (
        <MainAsideLayout
          header={blocks.header}
          aside={[
            blocks.infos,
            ...(player.cardSupply?.length ? [blocks.cardSupply] : []),
            blocks.careerStats,
            blocks.transfers,
            blocks.relatedPlayers,
            blocks.officialProduct,
          ]}
        >
          {blocks.performance}
          {blocks.priceGraph}
          {blocks.games}
          {blocks.moreAbout}
        </MainAsideLayout>
      );
    }

    return (
      <Section>
        {blocks.header}
        {blocks.infos}
        {player.cardSupply?.length && blocks.cardSupply}
        {blocks.performance}
        {blocks.priceGraph}
        {blocks.games}
        {blocks.careerStats}
        {blocks.transfers}
        {blocks.relatedPlayers}
        {blocks.officialProduct}
        {blocks.moreAbout}
      </Section>
    );
  },
  {
    query: FOOTBALL_PLAYER_OVERVIEW_QUERY,
    queryOptions: ({ slug }: PageParams) => ({ variables: { slug } }),
  }
);
